import { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import SwiperClass from 'swiper';
import { Keyboard, Navigation, Pagination } from 'swiper/modules';

import { Modal, styled } from '@mui/material';

import { CloseButton } from '../buttons/close-button';
import VideoWithControls from '../media/video-with-controls';
import {
  buttonDisabledClass,
  NextButton,
  PaginationContainer,
  PrevButton,
} from './swiper-elements';

export const imageSizes = [
  { width: 330, height: 167 },
  { width: 625, height: 416 },
  { width: 972, height: 648 },
  { width: 1296, height: 864 },
];

export const MultimediaGalleryLightbox = ({
  slides,
  lightboxOpen,
  setLightboxOpen,
  activeLightboxIndex,
  setActiveLightboxIndex,
}: {
  slides: { image: React.ReactElement; video?: string }[];
  lightboxOpen: boolean;
  setLightboxOpen: (o: boolean) => void;
  activeLightboxIndex: number;
  setActiveLightboxIndex: (index: number) => void;
}) => {
  const [lightboxSwiperRef, setLightboxSwiperRef] =
    useState<SwiperClass | null>(null);
  const nextButton = useRef<HTMLButtonElement>(null);
  const prevButton = useRef<HTMLButtonElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    lightboxSwiperRef?.slideTo(activeLightboxIndex);
  }, [activeLightboxIndex, lightboxSwiperRef]);

  return (
    <StyledModal open={lightboxOpen} onClose={() => setLightboxOpen(false)}>
      <LightboxContent>
        <StyledCloseButton onClick={() => setLightboxOpen(false)} />

        <Swiper
          modules={[Navigation, Pagination, Keyboard]}
          spaceBetween={0}
          slidesPerView={1.33333}
          breakpoints={{
            0: { slidesPerView: 1.15, centeredSlides: false },
            900: { slidesPerView: 1.296, centeredSlides: true },
            1440: { slidesPerView: 1.33333, centeredSlides: true },
          }}
          navigation={{
            nextEl: nextButton.current,
            prevEl: prevButton.current,
            disabledClass: buttonDisabledClass,
          }}
          pagination={{
            el: paginationRef.current,
            clickable: true,
          }}
          centeredSlidesBounds={true}
          initialSlide={activeLightboxIndex}
          onSwiper={setLightboxSwiperRef}
          onSlideChange={(swiper) => {
            setActiveLightboxIndex(swiper.realIndex);
          }}
          keyboard={true}
          style={{
            maxHeight: '100%',
          }}
        >
          {slides.map(({ image, video }, index) => (
            <StyledSwiperSlide key={index}>
              <ImageContainer
                even={index % 2 === 0}
                isVideo={video !== undefined}
              >
                {video ? (
                  <VideoWithControls
                    desktopSources={[{ src: video, type: 'video/mp4' }]}
                    posterImage={null}
                    autoplay={index === activeLightboxIndex}
                  ></VideoWithControls>
                ) : (
                  image
                )}
              </ImageContainer>
            </StyledSwiperSlide>
          ))}
          <ButtonContainer slot="container-end">
            <PrevButton ref={prevButton} />
            <NextButton ref={nextButton} />
          </ButtonContainer>
          <PaginationContainer colorTheme="dark" ref={paginationRef} />
        </Swiper>
      </LightboxContent>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  zIndex: theme.zIndex.drawer + 1,
  display: 'flex',
}));

const LightboxContent = styled('div')(({ theme }) => ({
  '--spacing': '30px',
  '--spacing-video': '72px',
  '--aspect-ratio-image': '1',
  '--aspect-ratio-video': '330 / 167',
  '--aspect-ratio-swiper': '330 / 350',

  position: 'relative',
  maxWidth: '90%',
  width: '90%',
  maxHeight: '100vh',
  outline: 'none',
  margin: 'auto auto',
  aspectRatio: 'var(--aspect-ratio-swiper)',

  [theme.breakpoints.up('sm')]: {
    '--spacing': '24px',
    '--spacing-video': 'var(--spacing)',
    '--aspect-ratio-image': '625 / 416',
    '--aspect-ratio-video': '830 / 420',
    '--aspect-ratio-swiper': '750 / 470',
  },

  [theme.breakpoints.up('md')]: {
    '--spacing': '37.5px',
    '--aspect-ratio-image': '972 / 648',
    '--aspect-ratio-video': '1290 / 653',
    '--aspect-ratio-swiper': '1290 / 715',
  },

  [theme.breakpoints.up('lg')]: {
    '--spacing': '50px',
    '--aspect-ratio-image': '1296 / 864',
    '--aspect-ratio-video': '1720 / 871',
    '--aspect-ratio-swiper': '1720 / 954',
  },
}));

const ButtonContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  inset: '0 0 var(--pagination-height) 0',
}));

const StyledCloseButton = styled(CloseButton)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  zIndex: 2,
  color: 'var(--color-white)',

  svg: {
    width: '36px',
    height: '36px',

    [theme.breakpoints.up('lg')]: {
      width: '48px',
      height: '48px',
    },
  },
}));

const StyledSwiperSlide = styled(SwiperSlide, {
  shouldForwardProp: (prop) => prop !== 'even',
})(({ theme }) => ({
  display: 'inline-flex',
}));

const ImageContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'even' && prop !== 'isVideo',
})<{ even: boolean; isVideo: boolean }>(({ theme, even, isVideo }) => ({
  display: 'inline-flex',
  marginTop: even
    ? isVideo
      ? 'var(--spacing-video)'
      : 'var(--spacing)'
    : undefined,
  marginBottom: !even ? 'var(--spacing)' : undefined,
  aspectRatio: isVideo
    ? 'var(--aspect-ratio-video)'
    : 'var(--aspect-ratio-image)',
  width: '100%',
  height: '100%',

  '& .gatsby-image-wrapper': {
    width: '100%',
    height: '100%',
  },

  '& .gatsby-image-wrapper img, img': {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
  },
}));
